import { RefObject, useEffect, useRef, useState } from "react";

export type Direction = "left" | "right";

const extremityEpsilon = 50;

export function useCarouselInfos(
	containerWidth: number,
	itemWidth: number,
	itemsCount: number
): {
	scrollingDiv: RefObject<HTMLDivElement | null>;
	handleScroll: () => void;
	snap: (to: Direction) => void;
	scrollToEnd: () => void;
	isDisabled: (direction: Direction) => boolean;
} {
	const scrollingDiv = useRef<HTMLDivElement>(null);

	const maxVisibleElements = Math.floor(containerWidth / itemWidth);
	const allItemsDisplayed = maxVisibleElements >= itemsCount;

	const [start, setStart] = useState(true);
	const [end, setEnd] = useState(allItemsDisplayed);

	const isDisabled = (dir: Direction): boolean => allItemsDisplayed || (dir === "left" ? start : end);

	const handleScroll = (): void => {
		if (!scrollingDiv.current) {
			return;
		}
		const scrollLeft = scrollingDiv.current.scrollLeft;
		const scrollRight = scrollingDiv.current.scrollWidth - (scrollLeft + scrollingDiv.current.clientWidth);
		setStart(scrollLeft <= extremityEpsilon);
		setEnd(scrollRight <= extremityEpsilon);
	};

	useEffect(() => {
		handleScroll();
	}, [itemsCount]);

	const snap = (to: Direction): void => {
		if (!scrollingDiv.current) {
			return;
		}
		scrollingDiv.current.scrollTo({
			left: scrollingDiv.current.scrollLeft + itemWidth * maxVisibleElements * (to === "left" ? -1 : 1),
			behavior: "smooth",
		});
	};

	const scrollToEnd = (): void => {
		if (!scrollingDiv.current) {
			return;
		}
		scrollingDiv.current.scrollTo({
			left: scrollingDiv.current?.scrollWidth,
			behavior: "smooth",
		});
	};

	useEffect(() => {
		setEnd(allItemsDisplayed);
	}, [allItemsDisplayed]);

	return {
		scrollingDiv,
		handleScroll,
		snap,
		scrollToEnd,
		isDisabled,
	};
}
